import { useMemo } from 'react';
import { Stack, Grid, Card, CardHeader, CardContent, Box, TextField, Button, Tooltip, Typography } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { CrudFilters, getDefaultFilter } from '@refinedev/core';
import { List, useDataGrid, DateField, DeleteButton } from '@refinedev/mui';
import { useForm } from '@refinedev/react-hook-form';
import { Controller } from 'react-hook-form';

const TaggedItemsList = () => {
  const { dataGridProps, filters, search } = useDataGrid({
    onSearch: (params: { name: string }) => {
      const filters: CrudFilters = [];

      filters.push({
        field: 'content_type',
        operator: 'eq',
        value: params.name
      });

      return filters;
    },

    filters: {
      initial: [
        {
          field: 'content_type',
          operator: 'eq',
          value: ''
        }
      ]
    }
  });

  const { control, handleSubmit } = useForm({
    defaultValues: {
      name: getDefaultFilter('content_type', filters, 'eq') || ''
    }
  });

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'tag_display_name',
        headerName: 'Tag Name',
        width: 200,
        flex: 2,
        renderCell: params => {
          return (
            <Tooltip title={params.row.tag_display_name}>
              <Typography variant="body1" noWrap>
                {params.row.tag_display_name}
              </Typography>
            </Tooltip>
          );
        }
      },
      {
        field: 'tag_set_name',
        headerName: 'Tagset Name',
        flex: 2,
        renderCell: params => {
          return (
            <Tooltip title={params.row.tag_set_name}>
              <Typography variant="body1" noWrap>
                {params.row.tag_set_name}
              </Typography>
            </Tooltip>
          );
        }
      },
      {
        field: 'content_type_model',
        headerName: 'Content Type',
        flex: 2,
        renderCell: params => {
          return (
            <Tooltip title={params.row.content_type_model}>
              <Typography variant="body1" noWrap>
                {params.row.content_type_model}
              </Typography>
            </Tooltip>
          );
        }
      },
      {
        field: 'content_object',
        headerName: 'Content Object',
        flex: 2,
        renderCell: params => {
          return (
            <Tooltip title={params.row.content_object}>
              <Typography variant="body1" noWrap>
                {params.row.content_object}
              </Typography>
            </Tooltip>
          );
        }
      },
      {
        field: 'created',
        headerName: 'Created At',
        renderCell: ({ value }) => <DateField format="LLL" value={value} />,
        flex: 1,
        sortable: false
      },
      {
        field: 'modified',
        headerName: 'Updated At',
        renderCell: ({ value }) => <DateField format="LLL" value={value} />,
        flex: 1,
        sortable: false
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 250,
        sortable: false,
        renderCell: params => {
          return (
            <Stack direction="row" spacing={1}>
              <DeleteButton
                hideText
                recordItemId={params.row.id}
                confirmTitle={
                  'Do you want to delete Tagged Item for Tag "' +
                  params.row.tag_display_name +
                  '" and Object "' +
                  params.row.content_object +
                  '" ?'
                }
              />
            </Stack>
          );
        }
      }
    ],
    []
  );

  // Remove sorting until https://github.com/refinedev/refine/issues/7099 is fixed
  const { sortingMode, sortModel, onSortModelChange, ...restDataGridProps } = dataGridProps;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={3}>
        <Card>
          <CardHeader title="Filters" />
          <CardContent>
            <Box
              component="form"
              autoComplete="off"
              sx={{
                display: 'flex',
                flexDirection: 'column'
              }}
              onSubmit={handleSubmit(search)}
            >
              <Controller
                name="name"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value || ''}
                    onChange={onChange}
                    label="Content Type"
                    placeholder="Search by Content Type"
                    margin="normal"
                    fullWidth
                    autoFocus
                  />
                )}
              />
              <Button type="submit" variant="contained">
                Apply
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} lg={9}>
        <List canCreate>
          <DataGridPro {...restDataGridProps} filterModel={undefined} columns={columns} disableColumnMenu pagination />
        </List>
      </Grid>
    </Grid>
  );
};

export default TaggedItemsList;
